import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useRoomStore } from './roomStore';
import { useSoulSelfStore } from './soulSelfStore';
import { useSessionStore } from './sessionStore';
import { logger } from '../utils/logger';
// Get API URL from environment variable (set at build time)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1';
const MAX_RETRY_COUNT = 5;
const RETRY_DELAYS = [1000, 2000, 4000, 8000, 16000]; // 指数バックオフ
export const useSSEStore = create()(devtools((set, get) => ({
    eventSource: null,
    connectionStatus: 'disconnected',
    lastError: null,
    retryCount: 0,
    currentUrl: null,
    lastEvent: null,
    cleanupIntervalId: null,
    // ネストしたストア
    useRoomStore,
    useSoulSelfStore,
    useSessionStore,
    connectByUrl(url) {
        try {
            const eventSource = new EventSource(url);
            eventSource.onopen = () => {
                logger.log('[Frontend] SSE connection opened');
                get().handleOpen();
            };
            // 名前なしイベント用
            eventSource.onmessage = (event) => {
                logger.log('[Frontend] SSE onmessage triggered');
                get().handleMessage(event);
            };
            // 名前付きイベント用のリスナーを追加
            const eventTypes = [
                'connected', 'soul_connected', 'soul_disconnected',
                'soul_died', 'soul_revived', 'soul_dived', 'soul_surfaced',
                'soul_updated', 'soul_kicked', 'room_closing', 'room_closed', 'ping'
            ];
            eventTypes.forEach(eventType => {
                eventSource.addEventListener(eventType, (event) => {
                    logger.log(`[Frontend] SSE ${eventType} event received`);
                    get().handleMessage(event);
                });
            });
            eventSource.onerror = (error) => {
                logger.warn('[Frontend] SSE error:', error);
                get().handleError(error);
            };
            set({ eventSource });
        }
        catch (error) {
            set({
                connectionStatus: 'error',
                lastError: error instanceof Error ? error.message : 'Connection failed'
            });
        }
    },
    connect: (roomId, sessionToken, password) => {
        const { disconnect, connectByUrl } = get();
        // 既存の接続をクリーンアップ
        disconnect();
        let currentUrl = API_BASE_URL + `/rooms/${roomId}/sse?token=${encodeURIComponent(sessionToken)}`;
        // 合言葉がある場合はパラメータに追加
        if (password) {
            currentUrl += `&password=${encodeURIComponent(password)}`;
        }
        set({
            connectionStatus: 'connecting',
            currentUrl,
            lastError: null
        });
        connectByUrl(currentUrl);
    },
    reconnect: () => {
        const { disconnect, connectByUrl, currentUrl } = get();
        // 既存の接続をクリーンナップ
        disconnect();
        connectByUrl(currentUrl);
    },
    disconnect: () => {
        const { eventSource, cleanupIntervalId } = get();
        if (eventSource) {
            logger.log('[Frontend] SSE Closed');
            eventSource.close();
        }
        // クリーンアップタイマーを停止
        if (cleanupIntervalId !== null) {
            clearInterval(cleanupIntervalId);
        }
        set({
            eventSource: null,
            connectionStatus: 'disconnected',
            currentUrl: null,
            retryCount: 0,
            lastError: null,
            cleanupIntervalId: null
        });
    },
    handleOpen: () => {
        // 既存のクリーンアップタイマーがあればクリア
        const { cleanupIntervalId } = get();
        if (cleanupIntervalId !== null) {
            clearInterval(cleanupIntervalId);
        }
        // 5分ごとにクリーンアップを実行
        const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5分
        const intervalId = setInterval(() => {
            useRoomStore.getState().cleanupOldSouls();
        }, CLEANUP_INTERVAL);
        set({
            connectionStatus: 'connected',
            retryCount: 0,
            lastError: null,
            cleanupIntervalId: intervalId
        });
        logger.log('SSE connection established');
    },
    handleMessage: (event) => {
        logger.log('[Frontend] Raw SSE data received:', event.data);
        try {
            const data = JSON.parse(event.data);
            logger.log('[Frontend] Parsed SSE event:', data);
            // lastEventを更新（システムメッセージ生成用）
            set({ lastEvent: data });
            // イベントタイプに応じて適切なストアを更新
            switch (data.type) {
                case 'message':
                    // 新しいメッセージを追加
                    const messageData = data.data;
                    useRoomStore.getState().addMessage(messageData);
                    // メッセージ送信者をrecentActiveSoulsに追加/更新（アクティビティとして）
                    useRoomStore.getState().addRecentActiveSoul({
                        id: messageData.soulId,
                        displayName: messageData.senderState.displayName,
                        isAnonymous: true,
                        depth: messageData.senderState.depth,
                        pain: messageData.senderState.pain,
                        suffering: messageData.senderState.suffering,
                        isDead: messageData.senderState.isDead,
                        deathTime: null,
                        deathCount: 0,
                        depthUpdatedAt: null,
                        painUpdatedAt: null,
                        sufferingUpdatedAt: null,
                        lastActivity: messageData.senderLastActivity || Date.now()
                    });
                    // 古いSoulをクリーンアップ
                    useRoomStore.getState().cleanupOldSouls();
                    // 自分のメッセージの場合、pain/sufferingを更新
                    const currentSoul = useSoulSelfStore.getState().soulSelf;
                    if (currentSoul && messageData.soulId === currentSoul.id) {
                        useSoulSelfStore.getState().updateSoulSelf({
                            pain: messageData.senderState.pain,
                            suffering: messageData.senderState.suffering,
                            depth: messageData.senderState.depth,
                            isDead: messageData.senderState.isDead
                        });
                    }
                    break;
                case 'soul_updated':
                    // Soul情報の更新
                    const soulUpdate = data.data;
                    if (soulUpdate.soulId) {
                        // 現在のSoulが更新対象の場合
                        const currentSoul = useSoulSelfStore.getState().soulSelf;
                        if (currentSoul && currentSoul.id === soulUpdate.soulId) {
                            useSoulSelfStore.getState().updateSoulSelf({
                                displayName: soulUpdate.displayName
                            });
                        }
                        // Room内のSoulも更新
                        useRoomStore.getState().updateRecentActiveSoul(soulUpdate.soulId, {
                            displayName: soulUpdate.displayName
                        });
                    }
                    break;
                case 'soul_connected':
                    // 新しく接続したSoulをrecentActiveSoulsに追加
                    const connectedSoul = data.data;
                    useRoomStore.getState().addRecentActiveSoul({
                        id: connectedSoul.soulId,
                        displayName: connectedSoul.displayName,
                        isAnonymous: true, // デフォルト値（SSEペイロードには含まれない）
                        depth: connectedSoul.depth,
                        pain: 0, // デフォルト値（SSEペイロードには含まれない）
                        suffering: 0, // デフォルト値（SSEペイロードには含まれない）
                        isDead: connectedSoul.isDead,
                        deathTime: null, // デフォルト値（SSEペイロードには含まれない）
                        deathCount: 0, // デフォルト値（SSEペイロードには含まれない）
                        depthUpdatedAt: null, // デフォルト値（SSEペイロードには含まれない）
                        painUpdatedAt: null, // デフォルト値（SSEペイロードには含まれない）
                        sufferingUpdatedAt: null, // デフォルト値（SSEペイロードには含まれない）
                        lastActivity: connectedSoul.lastActivity || Date.now()
                    });
                    // 参加者履歴にも追加
                    const currentRoom = useRoomStore.getState().room;
                    if (currentRoom) {
                        useRoomStore.getState().addParticipant(currentRoom.id, {
                            soulId: connectedSoul.soulId,
                            displayName: connectedSoul.displayName,
                            lastJoinedAt: connectedSoul.lastActivity || Date.now()
                        });
                    }
                    break;
                case 'soul_disconnected':
                    // 切断イベントではrecentActiveSoulsを変更しない（実際のアクティビティのみで管理）
                    break;
                case 'soul_died':
                    // Soul死亡イベント
                    logger.log('[Frontend] soul_died event received:', data.data);
                    const diedSoul = data.data;
                    // 自分のSoulの場合、状態を更新
                    const currentSoulDied = useSoulSelfStore.getState().soulSelf;
                    if (currentSoulDied && diedSoul.soulId === currentSoulDied.id) {
                        useSoulSelfStore.getState().updateSoulSelf({
                            depth: diedSoul.depth,
                            pain: diedSoul.pain,
                            suffering: diedSoul.suffering,
                            isDead: diedSoul.isDead,
                            deathTime: diedSoul.deathTime,
                            deathCount: diedSoul.deathCount
                        });
                    }
                    // Room内のSoulも更新
                    useRoomStore.getState().updateRecentActiveSoul(diedSoul.soulId, {
                        depth: diedSoul.depth,
                        pain: diedSoul.pain,
                        suffering: diedSoul.suffering,
                        isDead: diedSoul.isDead,
                        deathTime: diedSoul.deathTime,
                        deathCount: diedSoul.deathCount,
                        lastActivity: diedSoul.lastActivity
                    });
                    useRoomStore.getState().cleanupOldSouls();
                    break;
                case 'soul_revived':
                    // Soul復活イベント
                    logger.log('[Frontend] soul_revived event received:', data.data);
                    const revivedSoul = data.data;
                    // 自分のSoulの場合、状態を更新
                    const currentSoulRevived = useSoulSelfStore.getState().soulSelf;
                    if (currentSoulRevived && revivedSoul.soulId === currentSoulRevived.id) {
                        useSoulSelfStore.getState().updateSoulSelf({
                            depth: revivedSoul.depth,
                            pain: revivedSoul.pain,
                            suffering: revivedSoul.suffering,
                            isDead: revivedSoul.isDead,
                            deathTime: null,
                            deathCount: revivedSoul.deathCount
                        });
                    }
                    // Room内のSoulも更新
                    useRoomStore.getState().updateRecentActiveSoul(revivedSoul.soulId, {
                        depth: revivedSoul.depth,
                        pain: revivedSoul.pain,
                        suffering: revivedSoul.suffering,
                        isDead: revivedSoul.isDead,
                        deathTime: null,
                        deathCount: revivedSoul.deathCount,
                        lastActivity: revivedSoul.lastActivity
                    });
                    useRoomStore.getState().cleanupOldSouls();
                    break;
                case 'soul_dived':
                    // Soul落下イベント
                    logger.log('[Frontend] soul_dived event received:', data.data);
                    const divedSoul = data.data;
                    // dive行為をrecentActiveSoulsに追加/更新（アクティビティとして）
                    useRoomStore.getState().addRecentActiveSoul({
                        id: divedSoul.soulId,
                        displayName: divedSoul.displayName,
                        isAnonymous: true,
                        depth: divedSoul.depth,
                        pain: divedSoul.pain,
                        suffering: divedSoul.suffering,
                        isDead: divedSoul.isDead,
                        deathTime: divedSoul.deathTime,
                        deathCount: 0,
                        depthUpdatedAt: null,
                        painUpdatedAt: null,
                        sufferingUpdatedAt: null,
                        lastActivity: divedSoul.lastActivity || Date.now()
                    });
                    // 古いSoulをクリーンアップ
                    useRoomStore.getState().cleanupOldSouls();
                    // 自分のSoulの場合、状態を更新
                    const currentSoulDived = useSoulSelfStore.getState().soulSelf;
                    if (currentSoulDived && divedSoul.soulId === currentSoulDived.id) {
                        useSoulSelfStore.getState().updateSoulSelf({
                            depth: divedSoul.depth,
                            pain: divedSoul.pain,
                            suffering: divedSoul.suffering,
                            isDead: divedSoul.isDead,
                            deathTime: divedSoul.deathTime
                        });
                    }
                    break;
                case 'soul_surfaced':
                    // Soul浮上イベント
                    logger.log('[Frontend] soul_surfaced event received:', data.data);
                    const surfacedSoul = data.data;
                    // surface行為をrecentActiveSoulsに追加/更新（アクティビティとして）
                    useRoomStore.getState().addRecentActiveSoul({
                        id: surfacedSoul.soulId,
                        displayName: surfacedSoul.displayName,
                        isAnonymous: true,
                        depth: surfacedSoul.depth,
                        pain: surfacedSoul.pain,
                        suffering: surfacedSoul.suffering,
                        isDead: surfacedSoul.isDead,
                        deathTime: surfacedSoul.deathTime,
                        deathCount: 0,
                        depthUpdatedAt: null,
                        painUpdatedAt: null,
                        sufferingUpdatedAt: null,
                        lastActivity: surfacedSoul.lastActivity || Date.now()
                    });
                    // 古いSoulをクリーンアップ
                    useRoomStore.getState().cleanupOldSouls();
                    // 自分のSoulの場合、状態を更新
                    const currentSoulSurfaced = useSoulSelfStore.getState().soulSelf;
                    if (currentSoulSurfaced && surfacedSoul.soulId === currentSoulSurfaced.id) {
                        useSoulSelfStore.getState().updateSoulSelf({
                            depth: surfacedSoul.depth,
                            pain: surfacedSoul.pain,
                            suffering: surfacedSoul.suffering,
                            isDead: surfacedSoul.isDead,
                            deathTime: surfacedSoul.deathTime
                        });
                    }
                    break;
                case 'connected':
                    // 初回接続時の接続者リストを設定
                    const connectedData = data.data;
                    if (connectedData.connectedSouls && Array.isArray(connectedData.connectedSouls)) {
                        // 既存のrecentActiveSoulsをクリアしてconnectedSoulsで置き換え
                        const connectedSouls = connectedData.connectedSouls.map((soul) => ({
                            id: soul.soulId,
                            displayName: soul.displayName,
                            isAnonymous: true,
                            depth: soul.depth,
                            pain: 0,
                            suffering: 0,
                            isDead: soul.isDead,
                            deathTime: soul.deathTime || null,
                            deathCount: 0,
                            depthUpdatedAt: null,
                            painUpdatedAt: null,
                            sufferingUpdatedAt: null,
                            lastActivity: Date.now()
                        }));
                        useRoomStore.getState().setRecentActiveSouls(connectedSouls);
                    }
                    break;
                case 'soul_kicked':
                    // Soul追放イベント（Ban一覧に追加、Participants/ActiveSoulsから削除）
                    logger.log('[Frontend] soul_kicked event received:', data.data);
                    const kickedData = data.data;
                    // 自分がkickされた場合はstore更新をスキップ
                    // （Abyss.tsxで転送処理が実行される）
                    const currentSoulId = useSessionStore.getState().soulId;
                    if (kickedData.soulId === currentSoulId) {
                        logger.log('[Frontend] Self kicked, skipping store update');
                        break;
                    }
                    // 他人がkickされた場合のみstore更新
                    const currentRoomForBan = useRoomStore.getState().room;
                    if (currentRoomForBan) {
                        // 1. Ban一覧に追加（作成者のみが見る情報）
                        useRoomStore.getState().addBan(currentRoomForBan.id, {
                            soulId: kickedData.soulId,
                            displayName: kickedData.displayName || 'Unknown',
                            bannedAt: Date.now(),
                            bannedBy: kickedData.kickedBy || 'Unknown'
                        });
                        // 2. Participants一覧から削除
                        useRoomStore.getState().removeParticipant(kickedData.soulId);
                        // 3. ActiveSouls一覧から削除
                        useRoomStore.getState().removeRecentActiveSoul(kickedData.soulId);
                    }
                    break;
                case 'room_closing':
                    // ルーム閉鎖予告（システムメッセージのみ、状態変更なし）
                    logger.log('[Frontend] room_closing event received:', data.data);
                    break;
                case 'room_closed':
                    // ルーム閉鎖（ルームを非アクティブ化）
                    logger.log('[Frontend] room_closed event received:', data.data);
                    useRoomStore.getState().setRoomIsActive(false);
                    break;
                case 'ping':
                    // Keep-alive ping（何もしない）
                    break;
                default:
                    logger.warn('Unknown SSE event type:', data.type);
            }
        }
        catch (error) {
            logger.error('SSE message parsing error:', error);
            set({ lastError: 'Message parsing failed' });
        }
    },
    handleError: (error) => {
        // 一時的な接続エラー（pingタイムアウトなど）はwarnレベル
        logger.warn('SSE connection error:', error);
        const { eventSource, retryCount } = get();
        // EventSourceがCLOSED状態で即座に失敗した場合（403など）は再接続しない
        if (eventSource && eventSource.readyState === EventSource.CLOSED && retryCount === 0) {
            // 認証拒否などの致命的エラーはerrorレベル
            logger.error('SSE connection rejected (likely banned or unauthorized). Stopping reconnection.');
            set({
                connectionStatus: 'error',
                lastError: 'Connection rejected - you may have been banned',
                eventSource: null
            });
            return;
        }
        set({
            connectionStatus: 'error',
            lastError: 'Connection error occurred'
        });
        // 自動再接続ロジック
        if (retryCount < MAX_RETRY_COUNT) {
            const delay = RETRY_DELAYS[retryCount] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
            logger.log(`Retrying SSE connection in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRY_COUNT})`);
            setTimeout(() => {
                get().retry();
            }, delay);
        }
        else {
            logger.error('Max retry attempts reached. SSE connection failed permanently.');
            set({ lastError: 'Max retry attempts reached' });
        }
    },
    retry: () => {
        const { currentUrl, retryCount } = get();
        if (currentUrl && retryCount < MAX_RETRY_COUNT) {
            set({ retryCount: retryCount + 1 });
            get().reconnect();
        }
    },
    resetRetryCount: () => {
        set({ retryCount: 0 });
    },
    // ネストしたストアへのアクセス用メソッド
    getUseRoomStore: () => get().useRoomStore,
    getUseSoulSelfStore: () => get().useSoulSelfStore,
    getUseSessionStore: () => get().useSessionStore
}), {
    name: 'sse-store',
}));
